/**
 * DailyView — the site's landing view. Renders:
 *
 *   ┌─ view title (literal "每日文献动态") ─────────────────────────┐
 *   ├─ TopFilter chips (disease × type) — only inside DailyView ──┤
 *   ├─ per-day accordion(s) — most recent first, top one expanded  ┤
 *   └─ empty / loading / error state                                 ┘
 *
 * Data flow
 * ─────────
 * 1. `fetchDaily(7)` reads `/api/daily/:date` for the previous 7
 *    Beijing calendar days and bundles the response. Falls back to
 *    today's day-zero so a cold start with no ingest still shows the
 *    "暂无内容" empty state instead of blocking on a network error.
 * 2. The TopFilter's `onChange` callback updates an internal selection;
 *    a derived view of (groups + selection) renders the filtered card
 *    list. Selection state lives in this view; Round B may promote it
 *    to the URL `?d=…&t=…` query string.
 */

import { clear, el, setChildren } from "../dom";
import { ApiError, fetchDaily, fetchDailyByDate } from "../api";
import { topFilter, type TopFilterSelection } from "../components/topFilter";
import { dailyGroup } from "../components/dailyGroup";
import type { DailyDayGroup } from "../types";

const DAY_WINDOW = 7;

export function renderDailyView(): HTMLElement {
  const root = el("section", { class: "view view-daily", "data-view": "daily" });
  let selection: TopFilterSelection = { disease: null, type: null };
  let currentGroups: DailyDayGroup[] = [];
  let filterChip: HTMLElement | null = null;
  let info: HTMLElement | null = null;

  const status = el("div", { class: "status-row" }, [
    el("div", { class: "spinner", "aria-hidden": "true" }),
    el("div", { class: "status-row-title" }, "正在加载每日文献…"),
  ]);
  setChildren(root, [status]);

  // Kick off the fetch — render() must be synchronous so this races the
  // store subscription that mounted this view.
  void load();

  return root;

  async function load(): Promise<void> {
    try {
      const groups = await fetchDaily(DAY_WINDOW);
      currentGroups = groups;
      paint();
    } catch (err) {
      const error = err instanceof ApiError ? err : null;
      setChildren(root, [
        el("div", { class: "status-row is-error" }, [
          el("div", { class: "status-row-title" }, "无法加载"),
          el(
            "div",
            { class: "status-row-detail" },
            error ? `${error.status} · ${error.message}` : "网络错误，请稍后重试。",
          ),
        ]),
      ]);
    }
  }

  function paint(): void {
    const hasGroups = currentGroups.length > 0;
    const titleNode = el("div", { class: "view-title" }, [
      el("h1", null, "每日文献动态"),
      el("p", { class: "view-title-sub" }, `最近 ${DAY_WINDOW} 天的 PubMed 胸外科文献 · 北京时间`),
    ]);

    const filterNode = topFilter({
      initial: selection,
      onChange: (next) => {
        selection = next;
        paintBody();
      },
    });
    filterChip = filterNode;

    const bodyWrapper = el("div", { class: "main-pane view-body" });
    const list = el("div", { class: "day-list" });

    info = el("div", { class: "filter-info" });
    const toolbar = el("div", { class: "view-toolbar" }, [
      info,
      el("span", { class: "view-toolbar-meta" }, ""),
    ]);

    setChildren(root, [titleNode, filterNode, toolbar, bodyWrapper]);
    // re-mount the list under bodyWrapper.
    list.remove();
    bodyWrapper.appendChild(list);
    paintBody();

    function paintBody(): void {
      const filtered = applyFilter(currentGroups, selection);
      const total = currentGroups.reduce((acc, g) => acc + g.items.length, 0);
      const visibleTotal = filtered.reduce((acc, g) => acc + g.items.length, 0);

      // toolbar / info
      if (info) {
        if (selection.disease || selection.type) {
          const parts: string[] = [];
          if (selection.disease) parts.push(diseaseLabel(selection.disease));
          if (selection.type) parts.push(selection.type);
          setChildren(info, [
            el("span", null, `已筛选：${parts.join(" · ")}`),
            el("button", {
              type: "button",
              class: "filter-info-clear",
              onclick: () => {
                selection = { disease: null, type: null };
                paintBody();
                // re-paint filter chips to reflect cleared selection.
                if (filterNode?.parentNode) {
                  const refreshed = topFilter({
                    initial: selection,
                    onChange: (next) => {
                      selection = next;
                      paintBody();
                    },
                  });
                  filterNode.replaceWith(refreshed);
                  filterChip = refreshed;
                }
              },
            }, "清除"),
          ]);
        } else {
          setChildren(info, [
            el("span", null, `共 ${total} 篇文献`),
          ]);
        }
      }

      if (!hasGroups) {
        clear(list);
        list.appendChild(
          el("div", { class: "status-row" }, [
            el("div", { class: "status-row-title" }, "暂无内容"),
            el(
              "div",
              { class: "status-row-detail" },
              "尚未抓取到 PubMed 胸外科文献。请等待每日 00:00（北京时间）自动更新，或通过 /api/regenerate 手动触发。",
            ),
          ]),
        );
        return;
      }

      if (filtered.length === 0) {
        clear(list);
        list.appendChild(
          el("div", { class: "status-row" }, [
            el("div", { class: "status-row-title" }, "无匹配的文献"),
            el(
              "div",
              { class: "status-row-detail" },
              `在 ${total} 篇中找到 0 篇匹配项。请尝试调整筛选条件。`,
            ),
          ]),
        );
        return;
      }

      clear(list);
      filtered.forEach((group, idx) => {
        list.appendChild(
          dailyGroup({
            group,
            initiallyExpanded: idx === 0,
            // DailyListItem doesn't yet expose disease mapping;
            // Round B may extend `/api/daily/:date` to include a
            // `diseases: DiseaseKey[]` field and the predicate below
            // becomes `() => true` for daily items anyway. We do
            // optimistic substring filtering on the title for Round A.
            filter: selection.disease || selection.type
              ? (pmid: string) => matchesChip(group, pmid, selection)
              : undefined,
          }),
        );
      });

      // hide info block when no results
      if (visibleTotal === 0) {
        // already handled above
      }
    }
  }
}

function applyFilter(
  groups: DailyDayGroup[],
  selection: TopFilterSelection,
): DailyDayGroup[] {
  if (!selection.disease && !selection.type) return groups;
  return groups
    .map((g) => {
      const items = g.items.filter((it) => {
        return matchesChipGroup(it, selection);
      });
      return { epdat: g.epdat, items };
    })
    .filter((g) => g.items.length > 0);
}

function matchesChip(group: DailyDayGroup, pmid: string, sel: TopFilterSelection): boolean {
  const item = group.items.find((i) => i.pmid === pmid);
  if (!item) return false;
  return matchesChipGroup(item, sel);
}

/**
 * Round A optimistic filter — when the API doesn't yet expose disease
 * keys on each daily item, we fall back to a substring match against
 * the title for known disease keywords and `acronyms`. Once the worker
 * exposes `diseases: DiseaseKey[]` per item, swap this for a direct
 * `it.diseases?.includes(sel.disease)` check.
 */
function matchesChipGroup(
  item: { title: string; title_zh: string | null },
  sel: TopFilterSelection,
): boolean {
  if (sel.disease) {
    const en = item.title.toLowerCase();
    const zh = (item.title_zh ?? "").toLowerCase();
    const keywords: Record<string, string[]> = {
      lung_cancer: ["lung cancer", "nsclc", "sclc", "lung tumor", "肺癌"],
      mediastinal_tumor: ["mediastinal", "thymoma", "thymic", "纵隔"],
      esophageal_cancer: ["esophag", "esophageal cancer", "食管"],
      pneumothorax: ["pneumothorax", "rib fracture", "chest wall", "胸外伤", "肋骨", "胸壁", "气胸"],
    };
    const words = keywords[sel.disease] ?? [];
    const hit = words.some((w) => en.includes(w.toLowerCase()) || zh.includes(w.toLowerCase()));
    if (!hit) return false;
  }
  if (sel.type) {
    const en = item.title.toLowerCase();
    const zh = (item.title_zh ?? "").toLowerCase();
    if (sel.type === "人工智能/机器学习相关研究") {
      const words = ["machine learning", "deep learning", "neural network", "ai", "人工智能", "机器学习", "深度学习"];
      if (!words.some((w) => en.includes(w) || zh.includes(w))) return false;
    } else if (sel.type === "其他基础研究") {
      const basicWords = ["mechanism", "pathway", "molecular", "biomarker", "in vitro", "mouse", "rat ", "机制", "通路", "分子", "生物标志物"];
      if (!basicWords.some((w) => en.includes(w) || zh.includes(w))) return false;
    }
    // 临床研究: title doesn't have a strong keyword — currently accept
    // anything that didn't qualify for AI/ML or 其他基础研究. Round B
    // should rely on the weekly pipeline's classification or the worker
    // adding a `type` field to `daily_items`.
  }
  return true;
}

function diseaseLabel(key: TopFilterSelection["disease"]): string {
  switch (key) {
    case "lung_cancer": return "肺癌";
    case "mediastinal_tumor": return "纵隔肿瘤";
    case "esophageal_cancer": return "食管癌";
    case "pneumothorax": return "气胸（含胸部外伤、肋骨骨折、胸壁畸形）";
    default: return "全部";
  }
}

export const renderByDate = (date: string): HTMLElement => {
  // Round A shortcut — same view, but scoped to a single day.
  return renderDailyViewForDate(date);
};

export function renderDailyViewForDate(date: string): HTMLElement {
  // Reuse the main view for Round A; the title and toolbar reflect the
  // single-day scope so users know they're looking at one date.
  const root = el("section", { class: "view view-daily view-daily-by-date", "data-view": "daily-by-date" });
  const title = el("div", { class: "view-title" }, [
    el("h1", null, `每日文献动态 · ${date}`),
    el("p", { class: "view-title-sub" }, "单日范围（北京时区）"),
  ]);
  const status = el("div", { class: "status-row" }, [
    el("div", { class: "spinner" }),
    el("div", { class: "status-row-title" }, "正在加载…"),
  ]);
  setChildren(root, [title, status]);

  void (async (): Promise<void> => {
    try {
      const items = await fetchDailyByDate(date);
      const groups: DailyDayGroup[] = [{ epdat: date, items }];
      if (items.length === 0) {
        setChildren(root, [title, el("div", { class: "status-row" }, [
          el("div", { class: "status-row-title" }, "暂无内容"),
          el("div", { class: "status-row-detail" }, `日期 ${date} 暂无抓取到的文献。`),
        ])]);
        return;
      }
      const bodyWrapper = el("div", { class: "main-pane view-body" });
      const list = el("div", { class: "day-list" });
      list.appendChild(dailyGroup({ group: groups[0]!, initiallyExpanded: true }));
      bodyWrapper.appendChild(list);
      setChildren(root, [title, bodyWrapper]);
    } catch (err) {
      const error = err instanceof ApiError ? err : null;
      setChildren(root, [title, el("div", { class: "status-row is-error" }, [
        el("div", { class: "status-row-title" }, "无法加载"),
        el("div", { class: "status-row-detail" }, error ? `${error.status} · ${error.message}` : "网络错误"),
      ])]);
    }
  })();

  return root;
}
